import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  HeartHandshake, Plus, Search, Filter, RefreshCw, LayoutGrid, List,
  Clock, CreditCard, Activity, CheckCircle2, UserCheck, AlertCircle,
  Phone, Calendar, ChevronRight, ArrowRight, User, ShieldCheck, DollarSign,
  Send, ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';
import opheService from '../../services/ophe.service';
import OpheLeadCreateModal from './OpheLeadCreateModal';

export default function OphePipeline() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'all';

  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    stage1_payment: 0,
    stage2_active_care: 0,
    stage3_completed: 0,
    lost: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'table'
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    fetchPipelineData();
  }, [activeTab, selectedCategory]);

  const loadCategories = async () => {
    try {
      const res = await opheService.getCategories({ status: 'active' });
      setCategories(res.data?.data || []);
    } catch (err) {
      console.error('Failed to load categories', err);
    }
  };

  const fetchPipelineData = async () => {
    try {
      setLoading(true);
      const [sumRes, leadsRes] = await Promise.all([
        opheService.getPipelineSummary(),
        opheService.getPipelineLeads({
          tab: activeTab,
          categoryId: selectedCategory || undefined,
          limit: 100
        })
      ]);

      if (sumRes.data?.success) {
        setSummary(sumRes.data.data);
      }
      if (leadsRes.data?.success) {
        setLeads(leadsRes.data.data);
      }
    } catch (error) {
      console.error('Error loading OPHE pipeline:', error);
      toast.error('Failed to load OPHE pipeline data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  const filteredLeads = leads.filter(l => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    const name = l.patientName?.toLowerCase() || '';
    const phone = l.patientPhone?.toLowerCase() || '';
    const pkg = l.opheServiceDetails?.packageName?.toLowerCase() || '';
    const prov = l.opheProviderData?.providerName?.toLowerCase() || '';
    return name.includes(q) || phone.includes(q) || pkg.includes(q) || prov.includes(q);
  });

  // Stage Definition (Stage 1 = Payment)
  const kanbanStages = [
    {
      step: 1,
      id: 'payment',
      title: 'Stage 1: Payment',
      subtitle: 'Razorpay Link & Collection',
      color: 'border-amber-500 text-amber-700 bg-amber-50/50',
      badgeBg: 'bg-amber-100 text-amber-800',
      filter: (l) => l.displayStage === 1 && l.status !== 'not_interested'
    },
    {
      step: 2,
      id: 'active_care',
      title: 'Stage 2: Active Care',
      subtitle: 'Provider Assigned & Active',
      color: 'border-teal-500 text-teal-700 bg-teal-50/50',
      badgeBg: 'bg-teal-100 text-teal-800',
      filter: (l) => l.displayStage === 2 && l.status !== 'not_interested'
    },
    {
      step: 3,
      id: 'completed',
      title: 'Stage 3: Completed',
      subtitle: 'Care Settled & Payout Done',
      color: 'border-emerald-500 text-emerald-700 bg-emerald-50/50',
      badgeBg: 'bg-emerald-100 text-emerald-800',
      filter: (l) => l.displayStage === 3 || l.status === 'completed'
    }
  ];

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-[1600px] mx-auto min-h-screen">
      
      {/* 🚀 Header & Action Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-teal-800 via-teal-900 to-slate-900 p-6 rounded-3xl shadow-xl text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md border border-white/20 shadow-inner">
            <HeartHandshake className="w-8 h-8 text-teal-300" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-black tracking-tight">OPHE Care Pipeline</h1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-teal-400/20 text-teal-200 border border-teal-400/30">
                Stage 1: Payment Model
              </span>
            </div>
            <p className="text-xs text-teal-200/80 mt-0.5">
              Stage-by-Stage Elderly & Home Care Operations, Payments, Caregivers & Payouts
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchPipelineData()}
            className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white/90 hover:text-white border border-white/20 backdrop-blur-md transition-all flex items-center justify-center"
            title="Refresh Pipeline"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <Link
            to="/ophe-services"
            className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-xs border border-white/20 backdrop-blur-md transition-all flex items-center gap-2"
          >
            <span>Packages Catalog</span>
            <ChevronRight className="w-4 h-4" />
          </Link>

          <button
            onClick={() => setCreateModalOpen(true)}
            className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-400 active:bg-teal-600 text-slate-950 font-black text-xs shadow-lg shadow-teal-500/25 transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4 stroke-[3]" />
            <span>New OPHE Request</span>
          </button>
        </div>
      </div>

      {/* 📊 High-Level Metrics Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        
        {/* Total Active */}
        <div
          onClick={() => handleTabChange('all')}
          className={`cursor-pointer p-4 rounded-2xl border transition-all duration-200 ${
            activeTab === 'all'
              ? 'bg-white border-teal-600 shadow-md ring-2 ring-teal-500/20'
              : 'bg-white/80 border-slate-200/80 hover:bg-white hover:border-slate-300 shadow-sm'
          }`}
        >
          <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
            <span>All Active Cases</span>
            <Activity className="w-4 h-4 text-teal-600" />
          </div>
          <p className="text-2xl font-black text-slate-900 mt-2">{summary.total || 0}</p>
          <p className="text-[11px] text-slate-400 font-medium mt-0.5">Total pipeline records</p>
        </div>

        {/* Stage 1: Payment */}
        <div
          onClick={() => handleTabChange('payment')}
          className={`cursor-pointer p-4 rounded-2xl border transition-all duration-200 ${
            activeTab === 'payment'
              ? 'bg-white border-amber-600 shadow-md ring-2 ring-amber-500/20'
              : 'bg-white/80 border-slate-200/80 hover:bg-white hover:border-slate-300 shadow-sm'
          }`}
        >
          <div className="flex items-center justify-between text-amber-600 text-xs font-semibold">
            <span>Stage 1: Payment</span>
            <CreditCard className="w-4 h-4" />
          </div>
          <p className="text-2xl font-black text-amber-900 mt-2">{summary.stage1_payment || 0}</p>
          <p className="text-[11px] text-amber-600 font-medium mt-0.5">Payment pending / Link active</p>
        </div>

        {/* Stage 2: Active Care */}
        <div
          onClick={() => handleTabChange('active')}
          className={`cursor-pointer p-4 rounded-2xl border transition-all duration-200 ${
            activeTab === 'active'
              ? 'bg-white border-teal-600 shadow-md ring-2 ring-teal-500/20'
              : 'bg-white/80 border-slate-200/80 hover:bg-white hover:border-slate-300 shadow-sm'
          }`}
        >
          <div className="flex items-center justify-between text-teal-600 text-xs font-semibold">
            <span>Stage 2: Active Care</span>
            <UserCheck className="w-4 h-4" />
          </div>
          <p className="text-2xl font-black text-teal-900 mt-2">{summary.stage2_active_care || 0}</p>
          <p className="text-[11px] text-teal-600 font-medium mt-0.5">Provider assigned & active</p>
        </div>

        {/* Stage 3: Completed */}
        <div
          onClick={() => handleTabChange('completed')}
          className={`cursor-pointer p-4 rounded-2xl border transition-all duration-200 ${
            activeTab === 'completed'
              ? 'bg-white border-emerald-600 shadow-md ring-2 ring-emerald-500/20'
              : 'bg-white/80 border-slate-200/80 hover:bg-white hover:border-slate-300 shadow-sm'
          }`}
        >
          <div className="flex items-center justify-between text-emerald-600 text-xs font-semibold">
            <span>Stage 3: Completed</span>
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <p className="text-2xl font-black text-emerald-900 mt-2">{summary.stage3_completed || 0}</p>
          <p className="text-[11px] text-emerald-600 font-medium mt-0.5">Care concluded & payout settled</p>
        </div>

      </div>

      {/* 🔍 Search & Filter Toolbar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        
        {/* Left: Search input */}
        <div className="relative w-full md:w-96">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search patient, phone, package, provider..."
            className="w-full text-xs pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none transition-all"
          />
        </div>

        {/* Middle & Right: Category Filter & View Toggle */}
        <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-end">
          
          {/* Category dropdown */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="text-xs font-medium px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">All Care Categories</option>
            {categories.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>

          {/* View Mode Toggle */}
          <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                viewMode === 'kanban'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Kanban</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                viewMode === 'table'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <List className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Table</span>
            </button>
          </div>
        </div>

      </div>

      {/* 🚀 Main Content: Kanban or Table View */}
      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-400">
          <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
          <p className="text-sm font-bold text-slate-600">Loading OPHE Pipeline Cases...</p>
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200/80 p-12 text-center max-w-lg mx-auto shadow-sm my-8">
          <div className="w-16 h-16 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center mx-auto mb-4 border border-teal-100">
            <HeartHandshake className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-slate-800">No OPHE Cases Found</h3>
          <p className="text-xs text-slate-500 mt-1 mb-6">
            {searchTerm
              ? 'No cases match your search query.'
              : 'There are currently no cases in this pipeline view. Create a new OPHE care intake.'}
          </p>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs shadow-lg shadow-teal-600/20 transition-all inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create First OPHE Case</span>
          </button>
        </div>
      ) : viewMode === 'kanban' ? (
        /* KANBAN BOARD VIEW (3 STAGES) */
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {kanbanStages.map((stage) => {
            const stageLeads = filteredLeads.filter(stage.filter);
            return (
              <div
                key={stage.id}
                className="bg-slate-50/80 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-3 min-h-[520px]"
              >
                {/* Column Header */}
                <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                  <div>
                    <h3 className="font-bold text-sm text-slate-800 tracking-tight">{stage.title}</h3>
                    <p className="text-[11px] text-slate-400 font-medium">{stage.subtitle}</p>
                  </div>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-black ${stage.badgeBg}`}>
                    {stageLeads.length}
                  </span>
                </div>

                {/* Cards Container */}
                <div className="space-y-3 overflow-y-auto max-h-[calc(100vh-320px)] pr-1">
                  {stageLeads.length === 0 ? (
                    <div className="py-12 text-center text-slate-400 text-xs italic">
                      No cases in this stage
                    </div>
                  ) : (
                    stageLeads.map((lead) => (
                      <div
                        key={lead._id}
                        onClick={() => navigate(`/ophe/leads/${lead._id}`)}
                        className="bg-white rounded-xl border border-slate-200 hover:border-teal-500 hover:shadow-lg transition-all duration-200 p-4 cursor-pointer group relative overflow-hidden"
                      >
                        {/* Top Accent bar */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                        {/* Patient Name & Age */}
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className="font-bold text-sm text-slate-900 group-hover:text-teal-700 transition-colors">
                              {lead.patientName}
                            </h4>
                            <p className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                              <span>{lead.patientPhone}</span>
                              {lead.patientAge && <span>• {lead.patientAge}y</span>}
                              {lead.patientGender && <span className="capitalize">• {lead.patientGender}</span>}
                            </p>
                          </div>
                          
                          <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md ${
                            lead.priority === 'high'
                              ? 'bg-rose-100 text-rose-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {lead.priority || 'Medium'}
                          </span>
                        </div>

                        {/* Package Details */}
                        <div className="mt-3 bg-teal-50/50 rounded-lg p-2.5 border border-teal-100/60">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-bold text-teal-900 truncate">
                              {lead.opheServiceDetails?.packageName || 'OPHE Care Package'}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-teal-100 text-teal-800">
                              {lead.opheServiceDetails?.duration || 'Day'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mt-1.5 text-xs">
                            <span className="font-black text-slate-900">
                              ₹{lead.opheServiceDetails?.quotedPrice ? Number(lead.opheServiceDetails.quotedPrice).toLocaleString('en-IN') : '0'}
                            </span>
                            <span className={`text-[10px] font-bold ${
                              lead.ophePaymentData?.status === 'paid'
                                ? 'text-emerald-700'
                                : 'text-amber-700'
                            }`}>
                              {lead.ophePaymentData?.status === 'paid' ? '● Payment Done' : '○ Payment Pending'}
                            </span>
                          </div>
                        </div>

                        {/* Care Provider Assigned (if any) */}
                        {lead.opheProviderData?.providerName && (
                          <div className="mt-2.5 flex items-center gap-2 text-[11px] text-slate-600 bg-slate-50 p-2 rounded-lg border border-slate-100">
                            <UserCheck className="w-3.5 h-3.5 text-teal-600" />
                            <span className="font-semibold text-slate-800 truncate">
                              {lead.opheProviderData.providerName}
                            </span>
                            <span className="text-[10px] text-slate-400">({lead.opheProviderData.providerRole || 'Attendant'})</span>
                          </div>
                        )}

                        {/* Footer card link */}
                        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                          <span>Updated {new Date(lead.updatedAt || lead.createdAt).toLocaleDateString()}</span>
                          <span className="text-teal-600 font-bold group-hover:translate-x-0.5 transition-transform flex items-center gap-0.5">
                            Manage Stage {lead.displayStage || 1} <ChevronRight className="w-3 h-3" />
                          </span>
                        </div>

                      </div>
                    ))
                  )}
                </div>

              </div>
            );
          })}
        </div>
      ) : (
        /* DATA TABLE VIEW */
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                  <th className="py-3 px-4">Patient Profile</th>
                  <th className="py-3 px-4">Care Package</th>
                  <th className="py-3 px-4">Duration & Pricing</th>
                  <th className="py-3 px-4">Payment Status</th>
                  <th className="py-3 px-4">Assigned Provider</th>
                  <th className="py-3 px-4">Pipeline Stage</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredLeads.map((lead) => (
                  <tr key={lead._id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="font-bold text-slate-900">{lead.patientName}</div>
                      <div className="text-slate-500 text-[11px]">{lead.patientPhone}</div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="font-semibold text-slate-800">
                        {lead.opheServiceDetails?.packageName || 'OPHE Care'}
                      </span>
                      <div className="text-[11px] text-teal-600">
                        {lead.opheServiceDetails?.categoryId?.name || 'General Care'}
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="font-bold text-slate-900">
                        ₹{lead.opheServiceDetails?.quotedPrice ? Number(lead.opheServiceDetails.quotedPrice).toLocaleString('en-IN') : '0'}
                      </div>
                      <div className="text-[11px] text-slate-500">{lead.opheServiceDetails?.duration || 'Day'} package</div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        lead.ophePaymentData?.status === 'paid'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {lead.ophePaymentData?.status === 'paid' ? 'Paid' : 'Payment Due'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      {lead.opheProviderData?.providerName ? (
                        <div>
                          <span className="font-semibold text-slate-900">{lead.opheProviderData.providerName}</span>
                          <div className="text-[10px] text-slate-500">{lead.opheProviderData.providerRole}</div>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">Not Assigned</span>
                      )}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border ${
                        lead.displayStage === 1
                          ? 'bg-amber-50 text-amber-800 border-amber-200'
                          : lead.displayStage === 2
                          ? 'bg-teal-50 text-teal-800 border-teal-200'
                          : 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      }`}>
                        {lead.displayStage === 1
                          ? 'Stage 1: Payment'
                          : lead.displayStage === 2
                          ? 'Stage 2: Active Care'
                          : 'Stage 3: Completed'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <button
                        onClick={() => navigate(`/ophe/leads/${lead._id}`)}
                        className="px-3 py-1.5 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-700 font-bold text-xs transition-colors"
                      >
                        Open Case
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create OPHE Request Modal */}
      <OpheLeadCreateModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => fetchPipelineData()}
      />

    </div>
  );
}
